import type { NavigationItemInterface } from './interfaces';

export const GLOBAL_NAV_LINKS: NavigationItemInterface[] = [
  {
    label: 'Home',
    key: 'Home',
    uri: '/',
    description:
      'View a snapshot of your recent marketing performance and audience growth.',
    activePaths: ['/'],
  },
  {
    label: 'Campaigns',
    key: 'Campaigns',
    uri: '/campaigns',
    description:
      'View and manage your emails, ads, social posts, and landing pages.',
    activePaths: [
      '/campaigns',
      '/campaigns/edit',
      '/campaign-manager',
      '/campaign-manager/settings',
      '/campaigns/show-email',
    ],
  },
  {
    label: 'Automations',
    key: 'Automations',
    uri: '/customer-journey',
    children: [
      {
        label: 'All flows',
        key: 'All journeys',
        uri: '/customer-journey/my-journeys',
        description:
          'View and manage your automation flows and classic automations.',
        activePaths: [
          '/customer-journey/my-journeys',
          '/customer-journey/report',
        ],
      },
      {
        label: 'Flow templates',
        key: 'Pre-built journeys',
        uri: '/customer-journey/explore',
        description:
          'Get started with automations quickly by using templates for our most common flows.',
        activePaths: [
          '/customer-journey/explore',
          '/customer-journey/explore/prebuilt',
        ],
      },
      {
        label: 'Transactional email',
        key: 'Transactional email',
        uri: '/transactional',
        description:
          'Transactional Email is ideal for sending fast, highly personalized, one-to-one messages via API or SMTP.',
        activePaths: ['/transactional'],
      },
    ],
    description:
      'Map out a marketing automation flow that delivers a unique experience to each of your contacts.',
    activePaths: [
      '/customer-journey',
      '/customer-journey/explore',
      '/customer-journey/explore/prebuilt',
      '/customer-journey/my-journeys',
      '/transactional',
      '/customer-journey/report',
      '/customer-journey/create-new-journey',
    ],
  },
  {
    label: 'SMS',
    key: 'SMS',
    uri: '/sms',
    description: 'Send text message campaigns to your contacts.',
    badge: {
      type: 'new',
      label: 'New',
    },
    activePaths: ['/sms', '/sms/settings', '/sms/bulk'],
  },
  {
    label: 'Forms',
    key: 'Forms',
    uri: '/forms',
    children: [
      {
        label: 'Other forms',
        key: 'Other forms',
        uri: '/otherforms',
        description:
          'Create embedded forms and landing pages to reach more people.',
        activePaths: [
          '/otherforms',
          '/audience/forms/dashboard',
          '/audience/forms',
        ],
      },
    ],
    description:
      'Design and publish high-converting popups that precisely target your audience.',
    badge: {
      type: 'new',
      label: 'New',
    },
    activePaths: [
      '/forms',
      '/otherforms',
      '/audience/forms/dashboard',
      '/audience/forms',
      '/audience/forms/all-forms',
      '/audience/forms/create',
      '/forms/hosted-form/editor',
      '/forms/settings',
      '/forms/settings/edit-visitor-rule',
      '/forms/checklist',
      '/forms/details',
      '/forms/editor',
    ],
  },
  {
    label: 'Audience',
    key: 'Audience',
    uri: '/audience/contacts',
    children: [
      {
        label: 'Audience dashboard',
        key: 'Audience dashboard',
        uri: '/audience',
        description:
          'See how your audience is growing and engaging with your marketing.',
        activePaths: ['/audience', '/lists'],
      },
      {
        label: 'Tags',
        key: 'Tags',
        uri: '/audience/tags',
        description: 'Tag your contacts to organize them however you need to.',
        activePaths: ['/audience/tags', '/lists/tags/bulk-tag'],
      },
      {
        label: 'Segments',
        key: 'Segments',
        uri: '/audience/segments',
        description:
          'Filter contacts that meet certain criteria so you can send them targeted messages.',
        activePaths: ['/audience/segments'],
      },
      {
        label: 'Surveys',
        key: 'Surveys',
        uri: '/lists/surveys',
        description: 'Get insights by collecting feedback from your audience.',
        activePaths: ['/lists/surveys', '/lists/surveys/new'],
      },
      {
        label: 'Inbox',
        key: 'Inbox',
        uri: '/audience/create-audience',
        description:
          'Manage 1-1 conversations and responses from your audience.',
        activePaths: ['audience/inbox'],
      },
    ],
    description: 'View a list of all the contacts in your audience.',
    activePaths: [
      '/audience',
      '/lists',
      '/audience/subscriber-preferences',
      '/lists/surveys/new',
      '/audience/groups',
      '/audience/contacts',
      '/audience/add-contact',
      '/lists/archived-contacts',
      '/lists/members/unsubscribe-form',
      '/audience/import-history',
      '/audience/exports',
      '/lists/contact-exports',
      '/lists/members/archive-all',
      '/audience/manage',
      '/lists/settings/defaults',
      '/audience/gdpr-fields',
      '/lists/settings/publicity-settings',
      '/lists/settings/merge-tags',
      '/lists/settings/contact',
      '/lists/tools/import-email',
      '/lists/tools/analytics',
      '/lists/tools/webhooks',
      '/audience/settings',
      '/lists/integration/forms',
      '/lists/designer',
      '/audience/segments',
      '/audience/tags',
      '/lists/tags/bulk-tag',
      '/audience/contact-profile',
      '/audience/merge-fields',
    ],
  },
  {
    label: 'Analytics',
    key: 'Analytics',
    uri: '/analytics/marketing-dashboard/',
    children: [
      {
        label: 'Reports',
        key: 'Reports',
        uri: '/reports',
        description: 'See how your marketing performs with reports.',
        activePaths: [
          '/reports',
          '/reports/summary',
          '/reports/export',
          '/analytics/reports/overview',
        ],
      },
      {
        label: 'Custom reports',
        key: 'Custom reports',
        uri: '/analytics/reports/custom-reports',
        description: 'Get in-depth analysis of your marketing performance',
        activePaths: [
          '/analytics/custom-reports',
          '/analytics/custom-reports/builder',
          '/analytics/custom-reports/graph',
          '/analytics/custom-reports/details',
          '/analytics/reports/custom-reports',
          '/analytics/reports/custom-reports/builder',
          '/analytics/reports/custom-reports/graph',
          '/analytics/reports/custom-reports/details',
        ],
      },
    ],
    description:
      'Track the performance of your email and SMS campaigns with near real-time analytics.',
    activePaths: [
      '/analytics/email-dashboard',
      '/reports',
      '/analytics/custom-reports',
      '/analytics/marketing-dashboard',
      '/analytics/custom-reports/builder',
      '/analytics/custom-reports/graph',
      '/analytics/custom-reports/details',
      '/reports/summary',
      '/reports/export',
      '/analytics/reports/overview',
    ],
  },
  {
    label: 'Website',
    key: 'Website',
    uri: '/website/dashboard',
    children: [
      {
        label: 'Settings',
        key: 'Settings',
        uri: '/website/settings/',
        description:
          'Manage settings for your website, store, appointments, and more.',
        activePaths: ['/website/settings'],
      },
      {
        label: 'Reports',
        key: 'Reports',
        uri: '/website/reports/',
        description: 'View website reports.',
        activePaths: ['/website/reports'],
      },
    ],
    description:
      'Create and manage a website that comes with built-in marketing tools.',
    activePaths: [
      '/domains',
      '/website',
      '/websites',
      '/stores',
      '/appointments',
      '/website/settings',
      '/website/reports',
    ],
  },
  {
    label: 'Content',
    key: 'Content',
    uri: '/content/#myfiles',
    children: [
      {
        label: 'Creative Assistant',
        key: 'Creative Assistant',
        uri: '/creative-assistant',
        description:
          'Instantly create beautiful, on-brand designs you can use across marketing channels.',
        activePaths: ['/creative-assistant'],
      },
      {
        label: 'Email templates',
        key: 'Email templates',
        uri: '/templates',
        description:
          'View your saved templates or design a new one to fit your brand.',
        activePaths: ['/templates', '/templates/share-template'],
      },
      {
        label: 'Brand Kit',
        key: 'Brand Kit',
        uri: '/brand-kit',
        description:
          'Set up and manage your brand assets like colors, fonts, and more.',
        activePaths: ['/brand-kit'],
      },
    ],
    description: 'Upload and manage creative assets to use in your campaigns.',
    activePaths: ['/content'],
  },
  {
    label: 'Integrations',
    key: 'Integrations',
    uri: '/account/connected-sites/discover/',
    children: [
      {
        label: 'Manage',
        key: 'Manage',
        uri: '/integrations/manage',
        description:
          'Access features and settings related to your connected integrations.',
        activePaths: ['/integrations/connections/', '/integrations/manage'],
      },
    ],
    description: 'Discover new integrations.',
    activePaths: ['/account/connected-sites/discover'],
  },
];
